import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { toCamelCase } from "../../../hooks/toCameCase.tsx";
import StatusBadge from "./StatusBadge.tsx";
import EmptyImage from "../../../components/EmptyImage.tsx";
import { fixedHeadStyle, fixedStyle } from "../../../utils/constanst.js";

interface ClarificationTableProps {
  clarifications: any[];
  profileImages: { [key: string]: string };
  branchId: string;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  onSearch: (search: string) => void;
  onSort: (sortBy: string, order: "ASC" | "DESC") => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  currentSortBy?: string;
  currentOrder?: "ASC" | "DESC";
  searchTerm: string;
}

const ClarificationTable: React.FC<ClarificationTableProps> = ({
  clarifications,
  profileImages,
  branchId,
  total,
  page,
  limit,
  totalPages,
  onSearch,
  onSort,
  onPageChange,
  onLimitChange,
  currentSortBy = "createdAt",
  currentOrder = "DESC",
  searchTerm,
}) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debounce para la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearchTerm !== searchTerm) {
        onSearch(localSearchTerm);
        onPageChange(1); // Resetear a la primera página al buscar
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearchTerm]);

  const handleSort = (field: string) => {
    const newOrder =
      currentSortBy === field && currentOrder === "ASC" ? "DESC" : "ASC";
    onSort(field, newOrder);
  };

  const getSortIcon = (field: string) => {
    return (
      <i
        className={`fa-solid fa-sort-${currentOrder === "ASC" ? "up" : "down"}`}
      />
    );
  };

  const startIndex = (page - 1) * limit;
  const endIndex = Math.min(startIndex + clarifications.length, total);

  if (total === 0 && !searchTerm) {
    return (
      <div style={styles.emptyContainer}>
        <EmptyImage
          className="mx-auto w-250px"
          text="No hay aclaraciones registradas"
        />
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h3 style={styles.headerTitle}>
            <i className="fas fa-list-alt" style={styles.headerIcon}></i>
            Aclaraciones de Horario
          </h3>
          <span style={styles.badge}>Total: {total}</span>
        </div>
      </div>

      <div style={styles.searchContainer}>
        <div style={styles.searchWrapper}>
          <i className="fas fa-search" style={styles.searchIcon}></i>
          <input
            type="text"
            placeholder="Buscar por nombre del colaborador..."
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          {localSearchTerm && (
            <button
              onClick={() => {
                setLocalSearchTerm("");
                onSearch("");
                onPageChange(1);
              }}
              style={styles.clearButton}
              title="Limpiar búsqueda"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
      </div>

      <div
        className="table-responsive mb-3"
        style={{ overflowY: "auto", maxHeight: "800px" }}
      >
        <table className="table table-hover table-panel text-nowrap align-middle mb-0">
          <thead>
            <tr style={{ ...fixedHeadStyle } as React.CSSProperties}>
              <th
                style={{ ...fixedStyle } as React.CSSProperties}
                onClick={() => handleSort("id")}
              >
                ID {currentSortBy === "id" && getSortIcon("id")}
              </th>
              <th onClick={() => handleSort("partnerName")}>
                Colaborador{" "}
                {currentSortBy === "partnerName" && getSortIcon("partnerName")}
              </th>
              <th onClick={() => handleSort("checkDate")}>
                Fecha del Chequeo{" "}
                {currentSortBy === "checkDate" && getSortIcon("checkDate")}
              </th>
              <th>Comentario</th>
              <th onClick={() => handleSort("status")}>
                Estado {currentSortBy === "status" && getSortIcon("status")}
              </th>
              <th onClick={() => handleSort("createdAt")}>
                Fecha Solicitud{" "}
                {currentSortBy === "createdAt" && getSortIcon("createdAt")}
              </th>
              <th>Acción</th>
            </tr>
          </thead>
          <tbody>
            {clarifications.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="py-4">
                    <div className="d-flex">
                      <EmptyImage
                        className="mx-auto w-250px"
                        text="No se encontraron resultados"
                      />
                    </div>
                    <h5 className="text-secondary text-center fs-20px">
                      No se encontraron resultados para "{searchTerm}"
                    </h5>
                  </div>
                </td>
              </tr>
            ) : (
              clarifications.map((clarification) => (
                <tr key={clarification.id}>
                  <td style={{ ...fixedStyle } as React.CSSProperties}>
                    {clarification.id}
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
                        {toCamelCase(
                          `${clarification.partner?.person?.firstName || ""} ${
                            clarification.partner?.person?.lastName || ""
                          } ${
                            clarification.partner?.person?.secondLastName || ""
                          }`
                        )}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                        {clarification.partner?.candidate?.vacancy?.position
                          ?.name || "Sin puesto"}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: 500 }}>
                        {moment(clarification.checkDate).format("DD/MM/YYYY")}
                      </div>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#6b7280",
                          textTransform: "capitalize",
                        }}
                      >
                        {moment(clarification.checkDate).format("dddd")}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div
                      style={{
                        maxWidth: "300px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={clarification.comment}
                    >
                      {clarification.comment}
                    </div>
                  </td>
                  <td>
                    <StatusBadge status={clarification.status} />
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: 500 }}>
                        {moment(clarification.createdAt).format("DD/MM/YYYY")}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#6b7280" }}>
                        {moment(clarification.createdAt).format("HH:mm")}
                      </div>
                    </div>
                  </td>
                  <td>
                    <a
                      className="btn btn-azul m-2"
                      onClick={() =>
                        navigate(
                          `/sucursales/Aclaraciones-horario/${branchId}/revisar/${clarification.id}`
                        )
                      }
                      title="Revisar aclaración"
                    >
                      <i className="fa-solid fa-eye"></i>
                    </a>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.paginationContainer}>
        <div style={styles.paginationInfo}>
          Mostrando {startIndex + 1} - {endIndex} de {total} resultados
        </div>
        <div style={styles.paginationRight}>
          <div style={styles.paginationLimit}>
            <span style={styles.paginationLimitLabel}>Mostrar:</span>
            <select
              value={limit}
              onChange={(e) => {
                onLimitChange(Number(e.target.value));
                onPageChange(1);
              }}
              style={styles.paginationSelect}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          {totalPages > 1 && (
            <div style={styles.paginationControls}>
              <button
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page === 1}
                style={{
                  ...styles.paginationButton,
                  ...(page === 1 ? styles.paginationButtonDisabled : {}),
                }}
              >
                <i className="fas fa-chevron-left"></i>
                Anterior
              </button>
              <div style={styles.paginationNumbers}>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((pageNum) => {
                    // Mostrar primera, última, actual y adyacentes
                    return (
                      pageNum === 1 ||
                      pageNum === totalPages ||
                      (pageNum >= page - 1 && pageNum <= page + 1)
                    );
                  })
                  .map((pageNum, index, array) => {
                    // Agregar puntos suspensivos si hay gap
                    const showEllipsis =
                      index > 0 && array[index - 1] !== pageNum - 1;
                    return (
                      <React.Fragment key={pageNum}>
                        {showEllipsis && (
                          <span style={styles.paginationEllipsis}>...</span>
                        )}
                        <button
                          onClick={() => onPageChange(pageNum)}
                          style={{
                            ...styles.paginationNumber,
                            ...(page === pageNum
                              ? styles.paginationNumberActive
                              : {}),
                          }}
                        >
                          {pageNum}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>
              <button
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                style={{
                  ...styles.paginationButton,
                  ...(page === totalPages
                    ? styles.paginationButtonDisabled
                    : {}),
                }}
              >
                Siguiente
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    background: "#ffffff",
    borderRadius: "8px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    margin: "1.5rem",
    border: "1px solid #e5e7eb",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    background: "linear-gradient(135deg, #50B7C0 0%, #36a3ad 100%)",
    padding: "1rem 1.5rem",
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTitle: {
    margin: 0,
    fontSize: "1.125rem",
    fontWeight: 600,
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  headerIcon: {
    fontSize: "1.125rem",
  },
  badge: {
    background: "#ffffff",
    color: "#1f2937",
    padding: "0.5rem 0.75rem",
    borderRadius: "8px",
    fontSize: "0.875rem",
    fontWeight: 600,
  },
  searchContainer: {
    padding: "1rem 1.5rem",
    borderBottom: "1px solid #e5e7eb",
    background: "#f9fafb",
  },
  searchWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: "1rem",
    color: "#6b7280",
    fontSize: "0.875rem",
  },
  searchInput: {
    width: "100%",
    padding: "0.75rem 1rem 0.75rem 2.5rem",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "0.875rem",
    color: "#1f2937",
    outline: "none",
    transition: "all 0.2s ease",
  },
  clearButton: {
    position: "absolute",
    right: "0.75rem",
    background: "transparent",
    border: "none",
    color: "#6b7280",
    cursor: "pointer",
    padding: "0.25rem",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  },
  emptyContainer: {
    padding: "3rem",
    textAlign: "center",
  },
  paginationContainer: {
    padding: "1rem 1.5rem",
    borderTop: "1px solid #e5e7eb",
    background: "#f9fafb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "1rem",
  },
  paginationInfo: {
    fontSize: "0.875rem",
    color: "#6b7280",
  },
  paginationRight: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    flexWrap: "wrap",
  },
  paginationLimit: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  paginationLimitLabel: {
    fontSize: "0.875rem",
    color: "#374151",
    fontWeight: 500,
  },
  paginationSelect: {
    padding: "0.5rem 0.75rem",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    fontSize: "0.875rem",
    color: "#374151",
    background: "#ffffff",
    cursor: "pointer",
    outline: "none",
    transition: "all 0.2s ease",
  },
  paginationControls: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  paginationButton: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    padding: "0.5rem 1rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    transition: "all 0.2s ease",
  },
  paginationButtonDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  paginationNumbers: {
    display: "flex",
    alignItems: "center",
    gap: "0.25rem",
  },
  paginationNumber: {
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    padding: "0.5rem 0.75rem",
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    minWidth: "2.5rem",
    textAlign: "center",
    transition: "all 0.2s ease",
  },
  paginationNumberActive: {
    background: "linear-gradient(135deg, #50B7C0 0%, #36a3ad 100%)",
    color: "#ffffff",
    borderColor: "#50B7C0",
  },
  paginationEllipsis: {
    padding: "0 0.5rem",
    color: "#6b7280",
  },
};

export default ClarificationTable;
